import type {
  MembershipOption,
  MembershipOptionsV2,
  MembershipPricingOption,
  Prices,
  PriceWithMembershipOptions,
  PricingOption,
  PricingToggle,
  Maybe,
  RelatedVariant,
  ProductPricingDetailsType,
  Price
} from "@shared/graphql.types";
import { useStrikethrough } from "@shared/ui.hooks";
import { useCallback, useState, forwardRef, useEffect } from "react";
import { ProductATB, OutOfStock } from "@growth/page.products";
import { AccordionWithSingleMembershipOption } from "basket_and_checkout/shared/components/PDP/Pricing/AccordionWithSingleMembershipOption";
import { MembershipOptionsModal } from "packages/core-shopping/components/PDP/MembershipOptionsModal/MembershipOptionsModal";
import * as styles from "./PricingOptions.css";
import { dispatch } from "@shared/utils.events";
import { RelatedVariants } from "../RelatedVariants/RelatedVariants";
import { useCurrencySymbol } from "hooks";
import { ExperimentFlags } from "@shared/utils.experimentation";
import { UpsellModal } from "../UpsellModal";
import { useUpsellModal } from "../UpsellModal/useUpsellModal";
import { useSegment } from "tracking";

type Props = {
  membershipOptions: MembershipOptionsV2[];
  pricingOptions: PricingToggle;
  variant: { sku: string; prices: Prices };
  isInWishlist: boolean;
  handleWishlist: () => void;
  onAddToBag: ({
    membershipSKU,
    locationTypeDetails
  }: {
    membershipSKU: string;
    locationTypeDetails: string;
  }) => void;
  context?: string;
  relatedVariants?: Maybe<RelatedVariant[]>;
  selectedVariantSlug?: string;
  isOutOfStock?: boolean;
  handleSubmit: ({ email }: { email: string }) => Promise<boolean>;
  submitted: boolean;
  membershipInBag: boolean;
  isMember: boolean;
  isFreeTrialVariant: boolean;
  onPriceChange?: ((price: PricingOption) => void) | null;
  freeTrialPATBVariant?: number;
  isShadeMatchingEnabled?: boolean | null;
  hasMembership?: boolean;
};

const getDefaultMembershipOption = (
  price?: PricingOption
): MembershipOption | undefined => {
  if (!price) return;
  if ("memberships" in price) {
    return (price as PriceWithMembershipOptions).memberships.find(
      (membership) => membership.isDefault
    );
  }
  if ("membership" in price) {
    return (price as MembershipPricingOption).membership;
  }
};
const trackingDetailsMap = {
  1: "Guest Price",
  2: "Members Price"
};

const getCustomPrice = (
  promoPrice: ProductPricingDetailsType | null,
  regularPrice: ProductPricingDetailsType | null,
  showMembershipOptions: boolean,
  selectedPrice: Price | undefined
) => {
  if (promoPrice?.formatted) {
    return promoPrice?.formatted || regularPrice?.formatted || undefined;
  }

  if (showMembershipOptions) {
    return selectedPrice?.price.priceText;
  }

  return undefined;
};

export const PricingOptions = forwardRef<HTMLDivElement, Props>(
  (
    {
      membershipOptions,
      pricingOptions,
      variant,
      onAddToBag,
      handleWishlist,
      isInWishlist,
      context,
      relatedVariants = [],
      selectedVariantSlug,
      isOutOfStock = false,
      handleSubmit,
      submitted,
      membershipInBag,
      isMember,
      onPriceChange = null,
      freeTrialPATBVariant = -1,
      isShadeMatchingEnabled,
      hasMembership = false
    },
    ref
  ) => {
    const prices = pricingOptions.data.prices;
    const productPrices = pricingOptions.data.productPrices;
    const currencySymbol = useCurrencySymbol();
    const [isUpsellModalOpen, setIsUpsellModalOpen] = useState(false);
    const [showMembershipOptions, setShowMembershipOptions] = useState(
      !isMember && !membershipInBag
    );
    const { track } = useSegment();
    const { setShouldShowUpsellModal, getUpsellModalValue } = useUpsellModal();

    const defaultMembershipSku =
      membershipOptions?.find((membershipOption) => membershipOption.isDefault)
        ?.sku ?? membershipOptions?.[0]?.sku;

    const defaultMembership = membershipOptions?.find(
      (membershipOption) => membershipOption.isDefault
    );

    const defaultPrice = prices.find(
      (priceOption) => priceOption.id === pricingOptions.data.defaultPrice
    );

    const [selectedPrice, setSelectedPrice] = useState(defaultPrice);

    // Derive selectedMembershipSKU from selectedPrice to avoid state synchronization issues
    const selectedMembershipSKU = getDefaultMembershipOption(
      selectedPrice?.price
    )?.sku;

    const [isModalOpen, setIsModalOpen] = useState<string | null>(null);

    const onSelectMembership = async (
      chosenMembership: MembershipOptionsV2
    ) => {
      await onAddToBag?.({
        membershipSKU: chosenMembership.sku,
        locationTypeDetails: "membership_selection_panel"
      });
      setShowMembershipOptions(false);
      setIsModalOpen(null);
    };

    const handleUpsellModal = () => {
      setIsUpsellModalOpen(true);
      track({
        event: "Page Element Viewed",
        properties: {
          module_content: "ft_upsell_modal",
          location_type: "ft_upsell_modal"
        }
      });
    };

    const continueWithFreeTrial = (membershipSKU: string) => {
      setIsUpsellModalOpen(false);
      setShouldShowUpsellModal("0");

      track({
        event: "Page Element Clicked",
        properties: {
          module_content: "Add free trial"
        }
      });

      const option = prices.find(
        (priceOption) => priceOption.id === freeTrialPATBVariant?.toString()
      );

      if (!option) return;

      setSelectedPrice(option);

      if (onPriceChange) {
        setActiveUserType("member");
        onPriceChange(option.price);
      }
      onAddToBag({
        membershipSKU,
        locationTypeDetails: "ft_upsell_modal"
      });
      setShowMembershipOptions(false);
    };

    const continueWithoutFreeTrial = async (
      trackModuleContent = "No, Thanks"
    ) => {
      setIsUpsellModalOpen(false);
      setShouldShowUpsellModal("0");

      track({
        event: "Page Element Clicked",
        properties: {
          module_content: trackModuleContent
        }
      });

      onAddToBag({
        membershipSKU: "",
        locationTypeDetails: "ft_upsell_modal"
      });
    };

    const upsellModalSessionValue = getUpsellModalValue();

    useEffect(() => {
      if (upsellModalSessionValue === null) setShouldShowUpsellModal("1");
    }, [upsellModalSessionValue, setShouldShowUpsellModal]);

    const {
      promoPrice,
      price: regularPrice,
      setActiveUserType
    } = useStrikethrough(
      productPrices ?? undefined,
      hasMembership || selectedPrice?.id === "2"
    );

    const onAccordionChange = useCallback(
      (value: string) => {
        dispatch("page element clicked", {
          target: "PDP - Pricing Options",
          details: `${trackingDetailsMap[value]} selected`,
          location: "PDP - Core Product Info"
        });
        const option = prices.find((priceOption) => priceOption.id === value);

        if (!option) return;

        setSelectedPrice(option);

        if (onPriceChange) {
          const userType = (option.price as MembershipPricingOption).membership
            ? "member"
            : "guest";
          setActiveUserType(userType);
          onPriceChange(option.price);
        }
      },
      [prices, onPriceChange, setActiveUserType]
    );

    const shouldShowUpsellModal =
      defaultMembership &&
      showMembershipOptions &&
      freeTrialPATBVariant === 2 &&
      upsellModalSessionValue === "1" &&
      !selectedMembershipSKU;

    const handleAddToBag = () => {
      if (shouldShowUpsellModal) {
        handleUpsellModal();
        return;
      } else {
        onAddToBag({
          membershipSKU: selectedMembershipSKU || "",
          locationTypeDetails: "pdp-header"
        });

        if (selectedMembershipSKU) {
          setShowMembershipOptions(false);
        }
      }
    };

    const customPrice = getCustomPrice(
      promoPrice ?? null,
      regularPrice ?? null,
      showMembershipOptions,
      selectedPrice
    );
    const safeRelatedVariants = relatedVariants ?? [];
    const hasMultipleVariants = safeRelatedVariants.length > 1;

    return (
      <section data-testid="og_pdp_panel">
        {isOutOfStock ? (
          <div className={styles.relatedVariantContainer}>
            <hr className={styles.oosLineBreak} />

            {hasMultipleVariants && (
              <>
                <RelatedVariants
                  relatedVariants={relatedVariants ?? []}
                  selectedVariantSlug={selectedVariantSlug || ""}
                  title="Shade Options"
                  isShadeMatchingEnabled={isShadeMatchingEnabled}
                  currentSku={variant.sku}
                />
              </>
            )}

            <OutOfStock
              ref={ref}
              handleSubmit={handleSubmit}
              submitted={submitted}
            />
          </div>
        ) : (
          <>
            {showMembershipOptions && (
              <div
                data-event-intersection="experimentViewed"
                data-flag={
                  ExperimentFlags.PAGE_EXPERIMENT_PATB_AND_FT_EXPERIMENT
                }
                data-variation={freeTrialPATBVariant}
              >
                <AccordionWithSingleMembershipOption
                  {...{
                    selectedValue: selectedPrice?.id,
                    onAccordionChange,
                    prices: pricingOptions.data.prices || [],
                    productPrices: productPrices ?? undefined,
                    defaultMembershipSku,
                    setShowMembershipOptions: setIsModalOpen,
                    context
                  }}
                />
              </div>
            )}
            <div className={styles.relatedVariantContainer}>
              {hasMultipleVariants && (
                <div>
                  <hr className={styles.inStockLineBreak} />
                  <RelatedVariants
                    relatedVariants={relatedVariants ?? []}
                    selectedVariantSlug={selectedVariantSlug || ""}
                    title="Shade Options"
                    isShadeMatchingEnabled={isShadeMatchingEnabled}
                    currentSku={variant.sku}
                  />
                </div>
              )}

              <div className={styles.productATBWrapper.adaptive}>
                <ProductATB
                  isInWishlist={isInWishlist}
                  prices={variant.prices}
                  // NOTE: not currently on ProductATB type and not sure what the use of adding this was, need Charles' input
                  // variantSku={variant.sku}
                  // ! Do we need to actually use this with the new productPrices?
                  customPrice={customPrice}
                  handleWishlist={handleWishlist}
                  handleAddToBag={handleAddToBag}
                  context="desktop"
                  ref={ref}
                  isMember={isMember}
                  membershipInBag={membershipInBag}
                  currencySymbol={currencySymbol}
                />
              </div>
            </div>
          </>
        )}

        {selectedMembershipSKU && (
          <MembershipOptionsModal
            isOpen={!!isModalOpen}
            onClose={() => setIsModalOpen(null)}
            onSelect={onSelectMembership}
            initialMembershipSku={selectedMembershipSKU}
            memberships={membershipOptions}
            defaultMembershipSku={defaultMembershipSku}
          />
        )}
        {shouldShowUpsellModal && (
          <UpsellModal
            membership={defaultMembership}
            isOpen={isUpsellModalOpen}
            onDismiss={(trackModuleContent) =>
              continueWithoutFreeTrial(trackModuleContent)
            }
            onContinueWithFreeTrial={() =>
              continueWithFreeTrial(defaultMembershipSku)
            }
            onContinueWithWithoutFreeTrial={() => continueWithoutFreeTrial()}
          />
        )}
      </section>
    );
  }
);
